import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Colors for charts
const COLORS = ['#272757', '#20B2AA', '#f59e0b', '#4A0404', '#10b981', '#6366f1', '#f97316', '#8b5cf6', '#06b6d4', '#14b8a6'];

function DashboardNurturing() {

      const navigate = useNavigate();
      const location = useLocation();
    
      const token = localStorage.getItem('token');
      const email = localStorage.getItem('email');
      const role = localStorage.getItem('role');
    
      // lead states
      const [students, setStudents] = useState([]);
      const [professionals, setProfessionals] = useState([]);
      const [loading, setLoading] = useState(false);
      const [error, setError] = useState(null);
    
      // verify user effect (kept minimal)
      useEffect(() => {
        const verify = async () => {
          if (!token) return navigate('/login');
          try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/check-user`, { email, role, token });
            if (!res?.data?.success) {
              toast.error(res?.data?.message || 'Please login again');
              localStorage.clear();
              return navigate('/login');
            }
          } catch (err) {
            toast.error('Please login again');
            localStorage.clear();
            navigate('/login');
          }
        };
        verify();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [navigate, location.pathname]);
    
      // heuristic function to classify a lead as student or professional
      const classifyLead = (r) => {
        if (!r || typeof r !== 'object') return 'professional';
    
        // Normalize potential fields
        const toStr = (v) => (v === null || v === undefined ? '' : String(v).trim().toUpperCase());
        const fieldsToCheck = [
          r.LeadCategory, r.category, r.Type, r.type, r.role, r.Role, r.leadType, r.LeadType, r.LeadFor, r.lead_for,
          r.profile?.role, r.isStudent, r.isProfessional, r.student, r.professional, r.designation, r.title
        ];
    
        // direct boolean flags
        if (r.isStudent === true || r.student === true) return 'student';
        if (r.isProfessional === true || r.professional === true) return 'professional';
    
        // inspect strings for keywords
        const combined = fieldsToCheck.map(toStr).join(' ');
        if (combined.includes('STUDENT') || combined.includes('LEARNER') || combined.includes('FRESHER') || combined.includes('TRAINING')) return 'student';
        if (combined.includes('PROFESSIONAL') || combined.includes('WORK') || combined.includes('EMPLOYEE') || combined.includes('EXPERIENCED') || combined.includes('EMP') || combined.includes('PRO')) return 'professional';
    
        // fallback: check common email domains or other hints (optional)
        const emailStr = toStr(r.Email || r.email || r.contactEmail || r.contact_email || '');
        if (emailStr && (emailStr.includes('.edu') || emailStr.includes('student'))) return 'student';
    
        // default fallback -> professional (change to 'student' if your data skews that way)
        return 'professional';
      };
    
      useEffect(() => {
        const fetchAll = async () => {
          if (!token) return;
          setLoading(true);
          setError(null);
          try {
            // single demo endpoint
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/nurturing/all-nurturing-leads`, {
              headers: { Authorization: `Bearer ${token}` },
            });
    
            // response may be array or object with data property
            const raw = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
    
            // safety: if API returned object single item, wrap
            const arr = Array.isArray(raw) ? raw : [raw];
    
            const s = [];
            const p = [];
    
            arr.forEach((item, i) => {
              const key = item.Email ?? item.email ?? item._id ?? item.id ?? `lead-${i}`;
              const lead = { key, ...item };
    
              const cls = classifyLead(item);
              if (cls === 'student') s.push(lead);
              else p.push(lead);
            });
    
            setStudents(s.map((r, i) => ({ key: r.key ?? `s-${i}`, ...r })));
            setProfessionals(p.map((r, i) => ({ key: r.key ?? `p-${i}`, ...r })));
          } catch (err) {
            console.error(err);
            setError('Failed to fetch demo leads');
            toast.error('Failed to load demo leads');
          } finally {
            setLoading(false);
          }
        };
    
        fetchAll();
        // run when location.key changes (manual refresh or navigation)
      }, [token, location.key]);
    
      // Combined array helper
      const combinedLeads = useMemo(() => [...students, ...professionals], [students, professionals]);
    
      // Today / Pending leads count (simple representation: total leads fetched)
      const totalPending = combinedLeads.length;
    
      // Top 10 Lead Sources aggregated across students + professionals
      const topSources = useMemo(() => {
        const counts = {};
        const normalize = (v) => (v || '').toString().trim().toUpperCase();
        combinedLeads.forEach((r) => {
          // try several possible property names
          const raw = r.LeadSource || r.leadSource || r.source || r.Source || r.lead_source || r.SourceOfLead || '';
          const key = normalize(raw) || 'UNKNOWN';
          counts[key] = (counts[key] || 0) + 1;
        });
    
        const sorted = Object.entries(counts)
          .map(([name, cnt]) => ({ name, count: cnt }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);
    
        return sorted;
      }, [combinedLeads]);
    
      // Student vs Professional donut data
      const typeBreakdown = useMemo(() => {
        return [
          { name: 'Students', value: students.length },
          { name: 'Professionals', value: professionals.length },
        ];
      }, [students.length, professionals.length]);
    
      // Batch day aggregation (MORNING / EVENING)
      const batchDayData = useMemo(() => {
        const counts = { MORNING: 0, EVENING: 0, UNKNOWN: 0 };
        combinedLeads.forEach((r) => {
          const raw = r.BatchDay || r.batchDay || r.batch_day || r.batch || r.AvailabilityBatch || '';
          const key = (raw || '').toString().trim().toUpperCase();
    
          if (!key) counts.UNKNOWN += 1;
          else if (key.includes('MORN')) counts.MORNING += 1;
          else if (key.includes('EVE')) counts.EVENING += 1;
          else counts.UNKNOWN += 1;
        });
    
        const arr = [
          { name: 'MORNING', count: counts.MORNING },
          { name: 'EVENING', count: counts.EVENING },
        ];
        if (counts.UNKNOWN > 0) arr.push({ name: 'UNKNOWN', count: counts.UNKNOWN });
        return arr;
      }, [combinedLeads]);
    
      // --- Availability Batch (next 3 months) ---
      const availabilityNextThree = useMemo(() => {
        const addMonths = (d, m) => {
          const nd = new Date(d.getFullYear(), d.getMonth() + m, 1);
          return nd;
        };
    
        const now = new Date();
        const months = [0, 1, 2].map((off) => addMonths(now, off));
        const monthNames = ['JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'];
    
        const counts = {};
        months.forEach((mDate) => {
          const key = `${mDate.getFullYear()}-${mDate.getMonth()}`;
          counts[key] = 0;
        });
    
        const monthMap = monthNames.reduce((acc, name, idx) => {
          acc[name] = idx;
          acc[name.slice(0,3)] = idx; // allow short names
          return acc;
        }, {});
    
        combinedLeads.forEach((r) => {
          const raw = (r.AvailabilityBatch || r.availabilityBatch || r.availability_batch || r.Availability || '').toString().trim();
          if (!raw) return;
    
          let day = null, monthIdx = null, year = null;
          const parts = raw.split(/[-/\s]+/).filter(Boolean);
          if (parts.length >= 3) {
            const p0 = parts[0];
            const p1 = parts[1];
            const p2 = parts[2];
    
            if (/^[A-Za-z]+$/.test(p1)) {
              day = parseInt(p0, 10);
              const mName = p1.toUpperCase();
              monthIdx = monthMap[mName] ?? monthMap[mName.slice(0,3)];
              year = parseInt(p2, 10);
            } else if (/^[A-Za-z]+$/.test(p0)) {
              monthIdx = monthMap[p0.toUpperCase()];
              day = parseInt(p1, 10);
              year = parseInt(p2, 10);
            } else {
              const asNumbers = parts.map(x => parseInt(x,10));
              if (parts[0].length === 4) {
                year = asNumbers[0];
                monthIdx = asNumbers[1] - 1;
                day = asNumbers[2];
              } else if (parts[2].length === 4) {
                day = asNumbers[0];
                monthIdx = (asNumbers[1] || 1) - 1;
                year = asNumbers[2];
              }
            }
          }
    
          if (monthIdx == null || isNaN(year)) return;
          const key = `${year}-${monthIdx}`;
          if (Object.prototype.hasOwnProperty.call(counts, key)) counts[key] += 1;
        });
    
        const result = months.map((mDate) => {
          const key = `${mDate.getFullYear()}-${mDate.getMonth()}`;
          return { name: monthNames[mDate.getMonth()].slice(0,3), count: counts[key] || 0 };
        });
    
        return result;
      }, [combinedLeads]);
    
      const availabilityYMax = useMemo(() => {
        const max = Math.max(...availabilityNextThree.map((d) => d.count), 0);
        return Math.max(3, max + 1);
      }, [availabilityNextThree]);


      // --- CALL STATUS PIE DATA ---
const callStatusData = useMemo(() => {
  const counts = { PENDING: 0, INTERESTED: 0, "NOT INTERESTED": 0, "FOLLOW UP": 0 };

  combinedLeads.forEach((r) => {
    const raw = (r.CallStatus || r.callStatus || r.call_status || '').toString().trim().toUpperCase();

    if (raw.includes('PENDING')) counts.PENDING += 1;
    else if (raw.includes('INTERESTED') && !raw.includes('NOT')) counts.INTERESTED += 1;
    else if (raw.includes('NOT INTERESTED')) counts["NOT INTERESTED"] += 1;
    else if (raw.includes('FOLLOW')) counts["FOLLOW UP"] += 1;
    else counts.PENDING += 1; // fallback
  });

  return Object.entries(counts).map(([name, value]) => ({ name, value }));
}, [combinedLeads]);


// --- NEGOTIATION STATUS PIE DATA ---
const negotiationStatusData = useMemo(() => {
  const counts = { PENDING: 0, COMPLETED: 0 };

  combinedLeads.forEach((r) => {
    const raw = (r.NegotiationStatus || r.negotiationStatus || r.negotiation_status || '').toString().trim().toUpperCase();

    if (raw.includes('PENDING')) counts.PENDING += 1;
    else if (raw.includes('COMPLETE')) counts.COMPLETED += 1;
    else counts.PENDING += 1; // default fallback
  });

  return Object.entries(counts).map(([name, value]) => ({ name, value }));
}, [combinedLeads]);

// --- ENROLLMENT TYPE PIE DATA ---
const enrollmentTypeData = useMemo(() => {
  const counts = { PENDING: 0, 'JUNIOR PENETRATION TESTER': 0, 'SENIOR PENETRATION TESTER': 0 };

  combinedLeads.forEach((r) => {
    const raw = (r.EnrollmentType || r.enrollmentType || r.enrollment_type || '').toString().trim().toUpperCase();

    if (raw.includes('PENDING')) counts.PENDING += 1;
    else if (raw.includes('JUNIOR')) counts['JUNIOR PENETRATION TESTER'] += 1;
    else if (raw.includes('SENIOR')) counts['SENIOR PENETRATION TESTER'] += 1;
    else counts.PENDING += 1; // fallback
  });

  return Object.entries(counts).map(([name, value]) => ({ name, value }));
}, [combinedLeads]);

// --- INSTALLMENT PIE DATA ---
const installmentData = useMemo(() => {
  const counts = { PENDING: 0, YES: 0, NO: 0 };

  combinedLeads.forEach((r) => {
    const raw = (r.Installment || r.installment || r.installment_status || '').toString().trim().toUpperCase();

    if (raw.includes('PENDING')) counts.PENDING += 1;
    else if (raw === 'YES') counts.YES += 1;
    else if (raw === 'NO') counts.NO += 1;
    else counts.PENDING += 1; // fallback
  });

  return Object.entries(counts).map(([name, value]) => ({ name, value }));
}, [combinedLeads]);


  return (
    <div className="p-6 space-y-6">
          <ToastContainer
            position="bottom-left"
            autoClose={5000}
            hideProgressBar={true}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="dark"
            className="!mb-6 !ml-4"
            toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
          />
    
          {/* Header Section */}
          {/* <div className="p-2 text-center text-white bg-blue-950 rounded-xl">
            <h2 className="mt-2 text-2xl font-bold">NURTURING LEADS</h2>
          </div> */}
    
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

             <div className="grid grid-cols-1 gap-6">
            {/* Left: Today's Leads big card */}
            <div className="flex flex-col items-start justify-start col-span-1 p-4 bg-white shadow rounded-xl min -h-[100px]">
              <div className="flex items-center justify-between w-full mb-4">
                <h3 className="text-xl font-bold text-black ">TOTAL NURTURING LEADS</h3>
              </div>
    
              <div className="flex items-center justify-center w-10 h-10 mx-auto mt-4 rounded-md">
                <div className="text-center">
                  <div className="mt-5 font-bold text-7xl">{loading ? '...' : totalPending}</div>
                </div>
              </div>
            </div>
    
            {/* Middle: Top Sources list */}
            <div className="col-span-1 p-4 bg-white shadow lg:col-span-1 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-black">LEADS SOURCES</h3>
                <button className="text-xl font-bold text-black" onClick={() => window.location.reload()}>↻</button>
              </div>
    
              <div className="divide-y">
                {topSources.length === 0 && <div className="p-6 text-sm text-slate-400">No sources available</div>}
                {topSources.map((s, i) => (
                  <div key={s.name} className="flex items-center justify-between py-3">
                    <div className="text-sm font-semibold text-black">{i + 1}. {s.name}</div>
                    <div className="text-sm font-semibold text-black">{s.count}</div>
                  </div>
                ))}
              </div>
            </div>
            
    </div>
    
            {/* Right: Student vs Professional donut */}
            <div className="col-span-1 p-4 bg-white shadow rounded-xl">
    <h3 className="mb-2 text-xl font-bold text-black">LEAD CATEGORY</h3>
    <div style={{ width: '100%', height: 360 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={typeBreakdown}
            dataKey="value"
            nameKey="name"
            innerRadius={80}
            outerRadius={140}
            paddingAngle={1}
            label={(entry) => `${entry.name} (${entry.value})`}
          >
            {typeBreakdown.map((entry, index) => {
              // Assign your own colors for Students vs Professionals
              let color = '#94a3b8'; // default gray fallback
              if (entry.name === 'Students') color = '#0F059E';       // blue
              else if (entry.name === 'Professionals') color = '#1B5349'; // orange
              return <Cell key={`cell-${index}`} fill={color} />;
            })}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>

    {/* Optional Legend */}
    <div className="flex justify-center gap-6 mt-4 text-sm">
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 bg-blue-800 rounded-full"></span>
        <span className="text-slate-600">Students</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 bg-green-800 rounded-full"></span>
        <span className="text-slate-600">Professionals</span>
      </div>
    </div>
  </div>

  
          </div>

          {/* Lower row */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* <div className="p-6 bg-white shadow rounded-xl">
              <h3 className="mb-2 text-xl font-bold text-black">LEAD SOURCES DISTRIBUTION</h3>
              <div style={{ width: '100%', height: 360 }}>
                <ResponsiveContainer>
                  <BarChart data={topSources} layout="vertical" margin={{ left: 2 }}>
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={80} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" name="Count">
                      {topSources.map((entry, idx) => (
                        <Cell key={`bar-${idx}`} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div> */}
    
            {/* Batch Day Chart */}
            <div className="p-6 bg-white shadow rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="mb-2 text-xl font-bold text-black">BATCH DAY DISTRIBUTION</h3>
                <div className="text-sm text-black">
                  Total: <span className="font-semibold">{batchDayData.reduce((a, b) => a + b.count, 0)}</span>
                </div>
              </div>
    
              <div style={{ width: '100%', height: 360 }}>
                <ResponsiveContainer>
                  <BarChart data={batchDayData} margin={{ top: 20, right: 20, left: 10, bottom: 20 }}>
                    <defs>
                      <linearGradient id="gradMorning" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={1} />
                      </linearGradient>
                      <linearGradient id="gradEvening" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f97316" stopOpacity={1} />
                        <stop offset="100%" stopColor="#ef4444" stopOpacity={1} />
                      </linearGradient>
                      <linearGradient id="gradUnknown" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#94a3b8" stopOpacity={1} />
                        <stop offset="100%" stopColor="#64748b" stopOpacity={1} />
                      </linearGradient>
                    </defs>
    
                    <CartesianGrid strokeDasharray="6 4" horizontal={true} vertical={false} strokeWidth={2} stroke="#cbd5e1" />
    
                    <XAxis dataKey="name" type="category" axisLine={{ stroke: '#cbd5e1', strokeWidth: 3, strokeDasharray: '6 4' }} tickLine={false} tick={{ fill: '#475569', fontSize: 12, fontWeight: 500 }} padding={{ left: 20, right: 20 }} />
    
                    <YAxis domain={[0, 9]} allowDecimals={false} axisLine={false} tickLine={false} />
    
                    <Tooltip formatter={(value) => [value, 'Count']} wrapperStyle={{ borderRadius: 8 }} />
    
                    <Bar dataKey="count" name="Count" radius={[0, 0, 0, 0]} barSize={150} animationDuration={800}>
                      {batchDayData.map((entry, idx) => {
                        const id = entry.name === 'MORNING' ? 'gradMorning' : entry.name === 'EVENING' ? 'gradEvening' : 'gradUnknown';
                        return <Cell key={`cell-${idx}`} fill={`url(#${id})`} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
    
              <div className="flex justify-center gap-6 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ background: 'linear-gradient(180deg,#60a5fa,#3b82f6)' }}></span>
                  <span className="text-slate-600">Morning</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ background: 'linear-gradient(180deg,#f97316,#ef4444)' }}></span>
                  <span className="text-slate-600">Evening</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-slate-400"></span>
                  <span className="text-slate-600">Unknown</span>
                </div>
              </div>
            </div>
    
            {/* AVAILABILITY BATCH CHART (next 3 months) */}
            <div className="p-6 bg-white shadow rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="mb-2 text-xl font-bold text-black">AVAILABILITY BATCH (NEXT 3 MONTHS)</h3>
                <div className="text-sm text-black">Total: <span className="font-semibold">{availabilityNextThree.reduce((a,b) => a + b.count, 0)}</span></div>
              </div>
    
              <div style={{ width: '100%', height: 360 }}>
                <ResponsiveContainer>
                  <BarChart data={availabilityNextThree} margin={{ top: 20, right: 20, left: 10, bottom: 20 }}>
                    <defs>
                      <linearGradient id="gradA0" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#34d399" stopOpacity={1} />
                        <stop offset="100%" stopColor="#059669" stopOpacity={1} />
                      </linearGradient>
                      <linearGradient id="gradA1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={1} />
                      </linearGradient>
                      <linearGradient id="gradA2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f97316" stopOpacity={1} />
                        <stop offset="100%" stopColor="#ef4444" stopOpacity={1} />
                      </linearGradient>
                    </defs>
    
                    <CartesianGrid strokeDasharray="6 4" horizontal={true} vertical={false} stroke="#e6edf3" />
    
                    <XAxis dataKey="name" axisLine={{ stroke: '#cbd5e1', strokeWidth: 3, strokeDasharray: '6 4' }} tickLine={false} tick={{ fill: '#475569', fontSize: 13, fontWeight: 600 }} />
                    <YAxis domain={[0, availabilityYMax]} allowDecimals={false} axisLine={false} tickLine={false} />
    
                    <Tooltip formatter={(value) => [value, 'Count']} wrapperStyle={{ borderRadius: 8 }} />
    
                    <Bar dataKey="count" name="Count" radius={[0,0,0,0]} barSize={160} animationDuration={800}>
                      {availabilityNextThree.map((entry, idx) => (
                        <Cell key={`a-${idx}`} fill={`url(#gradA${idx % 3})`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
    
              <div className="flex justify-center gap-6 mt-4 text-sm">
                {availabilityNextThree.map((m, i) => (
                  <div key={`leg-${i}`} className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ background: `linear-gradient(180deg, ${i===0? '#34d399': i===1? '#60a5fa':'#f97316'}, ${i===0? '#059669': i===1? '#3b82f6':'#ef4444'})` }}></span>
                    <span className="text-slate-600">{m.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
    
           
    
          
          {/* GRID FOR 2-COLUMN PIE CHARTS */}
<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
  


  {/* CALL STATUS PIE CHART */}
 <div className="col-span-1 p-4 bg-white shadow rounded-xl">
  <h3 className="mb-2 text-xl font-bold text-black">CALL STATUS</h3>
  <div style={{ width: '100%', height: 360 }}>
    <ResponsiveContainer>
      <PieChart>
        <Pie
          data={callStatusData}
          dataKey="value"
          nameKey="name"
          innerRadius={80}
          outerRadius={140}
          paddingAngle={1}
          label={(entry) => `${entry.name} (${entry.value})`}
        >
          {callStatusData.map((entry, index) => {
            let color = '#94a3b8'; // default gray fallback
            if (entry.name === 'PENDING') color = '#721604'; // yellow
            else if (entry.name === 'INTERESTED') color = '#024519'; // green
            else if (entry.name === 'NOT INTERESTED') color = '#ef4444'; // red
            else if (entry.name === 'FOLLOW UP') color = '#3b82f6'; // blue
            return <Cell key={`cell-${index}`} fill={color} />;
          })}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  </div>

  {/* Optional Legend (custom, under chart) */}
  <div className="flex justify-center gap-6 mt-4 text-sm">
    <div className="flex items-center gap-2">
      <span className="w-3 h-3 bg-red-800 rounded-full"></span>
      <span className="text-slate-600">Pending</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="w-3 h-3 bg-green-800 rounded-full"></span>
      <span className="text-slate-600">Interested</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="w-3 h-3 bg-red-500 rounded-full"></span>
      <span className="text-slate-600">Not Interested</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
      <span className="text-slate-600">Follow Up</span>
    </div>
  </div>
</div>

{/* NEGOTIATION STATUS PIE CHART */}
<div className="col-span-1 p-4 bg-white shadow rounded-xl">
  <h3 className="mb-2 text-xl font-bold text-black">NEGOTIATION STATUS</h3>
  <div style={{ width: '100%', height: 360 }}>
    <ResponsiveContainer>
      <PieChart>
        <Pie
          data={negotiationStatusData}
          dataKey="value"
          nameKey="name"
         
          outerRadius={140}
          paddingAngle={1}
          label={(entry) => `${entry.name} (${entry.value})`}
        >
          {negotiationStatusData.map((entry, index) => {
            let color = '#94a3b8'; // default fallback gray
            if (entry.name === 'PENDING') color = '#721604'; // dark red
            else if (entry.name === 'COMPLETED') color = '#024519'; // dark green
            return <Cell key={`cell-${index}`} fill={color} />;
          })}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  </div>

  {/* Custom Legend */}
  <div className="flex justify-center gap-6 mt-4 text-sm">
    <div className="flex items-center gap-2">
      <span className="w-3 h-3 bg-red-900 rounded-full"></span>
      <span className="text-slate-600">Pending</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="w-3 h-3 bg-green-900 rounded-full"></span>
      <span className="text-slate-600">Completed</span>
    </div>
  </div>
</div>

{/* ENROLLMENT TYPE PIE CHART */}
<div className="col-span-1 p-4 bg-white shadow rounded-xl">
  <h3 className="mb-2 text-xl font-bold text-black">ENROLLMENT TYPE</h3>
  <div style={{ width: '100%', height: 360 }}>
    <ResponsiveContainer>
      <PieChart>
        <Pie
          data={enrollmentTypeData}
          dataKey="value"
          nameKey="name"
          innerRadius={5}
          outerRadius={140}
          paddingAngle={1}
          label={(entry) => `${entry.name} (${entry.value})`}
        > 
          {enrollmentTypeData.map((entry, index) => {
            let color = '#94a3b8'; // fallback gray
            if (entry.name === 'PENDING') color = '#7f1d1d'; // dark red
            else if (entry.name === 'JUNIOR PENETRATION TESTER') color = '#1e3a8a'; // dark blue
            else if (entry.name === 'SENIOR PENETRATION TESTER') color = '#064e3b'; // dark green
            return <Cell key={`cell-${index}`} fill={color} />;
          })}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  </div>

  {/* Custom Legend */}
  <div className="flex justify-center gap-6 mt-4 text-sm">
    <div className="flex items-center gap-2">
      <span className="w-3 h-3 bg-red-900 rounded-full"></span>
      <span className="text-slate-600">Pending</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="w-3 h-3 bg-blue-900 rounded-full"></span>
      <span className="text-slate-600">Junior Penetration Tester</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="w-3 h-3 bg-green-900 rounded-full"></span>
      <span className="text-slate-600">Senior Penetration Tester</span>
    </div>
  </div>
</div>

{/* INSTALLMENT PIE CHART */}
<div className="col-span-1 p-4 bg-white shadow rounded-xl">
  <h3 className="mb-2 text-xl font-bold text-black">PENDING INSTALLMENT UPDATION</h3>
  <div style={{ width: '100%', height: 360 }}>
    <ResponsiveContainer>
      <PieChart>
        <Pie
          data={installmentData}
          dataKey="value"
          nameKey="name"
          innerRadius={80}
          outerRadius={140}
          paddingAngle={1}
          label={(entry) => `${entry.name} (${entry.value})`}
        >
          {installmentData.map((entry, index) => {
            let color = '#94a3b8'; // fallback gray
            if (entry.name === 'PENDING') color = '#7f1d1d'; // dark red
            else if (entry.name === 'YES') color = '#064e3b'; // dark green
            else if (entry.name === 'NO') color = '#1e3a8a'; // dark blue
            return <Cell key={`cell-${index}`} fill={color} />;
          })}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  </div>

  {/* Custom Legend */}
  <div className="flex justify-center gap-6 mt-4 text-sm">
    <div className="flex items-center gap-2">
      <span className="w-3 h-3 bg-red-900 rounded-full"></span>
      <span className="text-slate-600">Pending</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="w-3 h-3 bg-green-900 rounded-full"></span>
      <span className="text-slate-600">Yes</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="w-3 h-3 bg-blue-900 rounded-full"></span>
      <span className="text-slate-600">No</span>
    </div>
  </div>
</div>



</div>

    
           
    
          
    
          
    
          {error && <div className="text-red-500">{error}</div>}
        </div>
  )
} 

export default DashboardNurturing